import * as Sentry from '@sentry/nextjs'

import { Listing, PaymentV3Product } from '../getListings'
import { Flags } from './types'

export type PaymentPayload = Pick<Listing, 'payment_v3'>

/**
 * Discriminated so a caller cannot reach for a price the payload does not
 * carry: there is no `total` to render unless the selection is `v3`.
 */
export type PaymentSelectionProduct = {
  /** Discounted financing core for this product. */
  base: number
  /**
   * The rendered monthly for this product: `base + taxMonthly + insuranceMonthly`,
   * with HOA folded in when `showHoa` is set.
   */
  total: number
}

export type PaymentSelection =
  | { kind: 'v2' }
  | {
      kind: 'v3'
      /**
       * Tax and insurance are the same for both products (they pass through at
       * full cost), so they ride along once here — the calculator can show a
       * breakdown and reach the same figures as the listing (PDE-80) instead of
       * recomputing from Contentful. Only the financing core differs per
       * product, so `base` and `total` are carried per product. `showHoa`
       * decides whether each product's `total` includes HOA.
       */
      taxMonthly: number
      insuranceMonthly: number
      hoaMonthly: number | null
      showHoa: boolean
      products: {
        saver: PaymentSelectionProduct
        boost: PaymentSelectionProduct
      }
    }
  | { kind: 'unavailable' }

export type UnavailableReason =
  /** The v3 gate did not pass upstream, so the key is absent. Expected. */
  | 'payment_v3_absent'
  /** An explicit null is a producer bug (see `isContractNull`). */
  | 'payment_v3_null'
  /**
   * A block present but without the per-product shape — reached during a
   * deploy-order race where a not-yet-updated backend still serves the old flat
   * block (no `products`). Degrade rather than throw and crash the render.
   */
  | 'payment_v3_malformed'
  | 'selected_total_not_finite'
  /**
   * `base` is a producer field in its own right, not derived from `total` at
   * this boundary, and the browse surfaces now render it directly, so a
   * non-finite base is declined the same way as a non-finite total.
   */
  | 'selected_base_not_finite'

/**
 * Gates the Sentry capture only. The selector runs once per tile — up to 23 a
 * page — so an undeduplicated capture would bury the signal under its own
 * volume. Same idiom as components/ListingContent/attribution/MlsMark.tsx.
 */
const reported = new Set<UnavailableReason>()

const emit = (reason: UnavailableReason) => {
  // The two channels differ on purpose: this log line is the counting channel,
  // and a fallback RATE is only measurable if every occurrence is emitted, so
  // it stays outside the de-duplication below. Never the terminal handler.
  console.warn(
    JSON.stringify({ event: 'v3_requested_but_unavailable', reason })
  )

  if (reported.has(reason)) {
    return
  }

  reported.add(reason)

  try {
    Sentry.captureMessage(`v3 pricing requested but unavailable: ${reason}`, {
      level: 'warning',
      tags: { event: 'v3_requested_but_unavailable', reason },
    })
  } catch {
    // Reporting must never be the thing that breaks a page render.
  }
}

/**
 * PDE-56 omits `payment_v3` rather than nulling it, so `null` is outside the
 * declared type and a direct `=== null` will not compile. Crossing the
 * boundary through `unknown` keeps a producer regression observable instead of
 * letting it read `.total` off null.
 */
const isContractNull = (value: unknown): value is null => value === null

export function selectPayment(
  payment: PaymentPayload,
  flags: Flags
): PaymentSelection {
  if (!flags.v3PricingCore) {
    return { kind: 'v2' }
  }

  const block = payment.payment_v3

  if (block === undefined) {
    emit('payment_v3_absent')

    return { kind: 'unavailable' }
  }

  if (isContractNull(block)) {
    emit('payment_v3_null')

    return { kind: 'unavailable' }
  }

  // Web and backend deploy independently, so a not-yet-updated backend can
  // still serve the old flat block (no `products`). Reading `block.products.saver`
  // off it would throw and crash the listing render, so treat any block missing
  // either product sub-block as unavailable rather than assuming the new shape.
  const products = (
    block as {
      products?: { saver?: PaymentV3Product; boost?: PaymentV3Product }
    }
  ).products
  if (!products?.saver || !products?.boost) {
    emit('payment_v3_malformed')

    return { kind: 'unavailable' }
  }

  const showHoa = flags.v3HoaDisplay
  const productSelection = (
    product: PaymentV3Product
  ): PaymentSelectionProduct => ({
    base: product.base,
    total: showHoa ? product.total_with_hoa : product.total,
  })
  const saver = productSelection(products.saver)
  const boost = productSelection(products.boost)

  // A non-finite total would render as "$NaN"; a zero fallback would render as
  // a real $0 price. Neither is shippable, so decline to quote instead.
  if (!Number.isFinite(saver.total) || !Number.isFinite(boost.total)) {
    emit('selected_total_not_finite')

    return { kind: 'unavailable' }
  }

  // base is rendered directly on the browse surfaces now, so guard it the same
  // way: a non-finite base would print "NaN" rather than declining to quote.
  if (!Number.isFinite(saver.base) || !Number.isFinite(boost.base)) {
    emit('selected_base_not_finite')

    return { kind: 'unavailable' }
  }

  return {
    kind: 'v3',
    taxMonthly: block.tax_monthly,
    insuranceMonthly: block.insurance_monthly,
    hoaMonthly: block.hoa_monthly,
    showHoa,
    products: { saver, boost },
  }
}

/** Test seam: the de-duplication set is module state by design. */
export const resetPaymentSelectionReporting = () => {
  reported.clear()
}
